import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { FileSignature, Download, Check, MapPin, Calendar, CheckCircle2, ShieldCheck, CreditCard, Loader2, MessageSquare, Send, Lock, LayoutList, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProposalView() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [amountMode, setAmountMode] = useState("retainer");
  const [customAmount, setCustomAmount] = useState("");
  const [sig, setSig] = useState({ name: "", date: new Date().toISOString().slice(0, 10) });
  const [note, setNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [signing, setSigning] = useState(false);
  const [chatText, setChatText] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const chatEndRef = useRef(null);
  const initialized = useRef(false);

  const reload = () => api.getProposal(id).then((data) => {
    setP(data);
    if (!initialized.current) {
      setAmountMode(data.retainer > 0 && data.retainer < (data.balance ?? data.total) - 0.01 && (data.amount_paid || 0) <= 0 ? "retainer" : "full");
      initialized.current = true;
    }
  }).catch(() => setErr(true));

  useEffect(() => {
    reload();
    const t = setInterval(reload, 6000); // keep chat + payment status fresh
    return () => clearInterval(t);
  }, [id]);

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;
    setSendingChat(true);
    try {
      await api.postProposalChat(id, { text, sender: "client" });
      setChatText("");
      await reload();
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally { setSendingChat(false); }
  };


  if (err) return <div className="min-h-screen clean-bg flex items-center justify-center text-zinc-400">This proposal could not be found.</div>;
  if (!p) return <div className="min-h-screen clean-bg flex items-center justify-center text-zinc-500">Loading proposal…</div>;

  const total = Number(p.total) || 0;
  const paid = Number(p.amount_paid) || 0;
  const balance = Number(p.balance ?? Math.max(total - paid, 0));
  const retainer = Number(p.retainer) || 0;
  const fullyPaid = p.fully_paid || balance <= 0.01;

  const chosenAmount = () => {
    if (amountMode === "retainer") return Math.min(retainer > 0 ? retainer : balance, balance);
    if (amountMode === "custom") return Number(customAmount) || 0;
    return balance;
  };

  const pay = async () => {
    const amount = Number(chosenAmount().toFixed(2));
    if (amount <= 0) { toast.error("Enter a valid amount to pay."); return; }
    if (amount > balance + 0.01) { toast.error("Amount exceeds the remaining balance."); return; }
    if (p.signature_required && !p.signature && (!sig.name.trim() || !sig.date)) {
      toast.error("Please type your name and date to approve this proposal.");
      return;
    }
    setPaying(true);
    try {
      const res = await api.payProposal(id, {
        origin_url: window.location.origin,
        amount,
        signature_name: p.signature_required && !p.signature ? sig.name.trim() : undefined,
        signature_date: p.signature_required && !p.signature ? sig.date : undefined,
        note: note.trim() || undefined,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  const sign = async () => {
    if (p.signature_required && !p.signature && (!sig.name.trim() || !sig.date)) {
      toast.error("Please type your name and date to approve this proposal.");
      return;
    }
    setSigning(true);
    try {
      await api.signProposal(id, {
        signature_name: sig.name.trim() || undefined,
        signature_date: sig.date,
      });
      toast.success("Approved! Your workroom is ready.");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve this proposal");
      setSigning(false);
    }
  };

  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const workspaceHref = p.workflow_id ? `/app/workspace/${p.workflow_id}` : null;
  const hasPaid = paid > 0;
  const chatLocked = p.chat_locked || hasPaid;
  const agreed = ["accepted", "deposit_paid", "paid"].includes(p.status) || hasPaid;

  return (
    <div className="min-h-screen clean-bg text-white" data-testid="proposal-view">
      <div className="glass-nav border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="text-xl font-extrabold font-display">DONDR<span className="text-primary-400">Á</span>E</div>
        <div className="flex items-center gap-2">
          {workspaceHref && (
            <a href={workspaceHref} className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition flex items-center" data-testid="proposal-workspace-btn"><LayoutList size={15} className="mr-2" /> Go to Workroom</a>
          )}
          <a href={api.proposalPdfUrl(id)} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-white/5 text-zinc-200 rounded-xl text-sm font-bold hover:bg-white/10 transition flex items-center" data-testid="proposal-download-pdf"><Download size={15} className="mr-2" /> Download PDF</a>
        </div>
      </div>

      {/* Top status banner */}
      {fullyPaid ? (
        <div className="bg-green-500/15 border-b border-green-500/30 px-6 py-3 flex items-center justify-center gap-2 text-green-300 font-bold text-sm" data-testid="proposal-paid-banner">
          <CheckCircle2 size={17} /> PAID IN FULL — thank you! This deal is confirmed.
        </div>
      ) : hasPaid ? (
        <div className="bg-teal-500/15 border-b border-teal-500/30 px-6 py-3 flex items-center justify-center gap-2 text-teal-300 font-bold text-sm" data-testid="proposal-deposit-banner">
          <CheckCircle2 size={17} /> DEPOSIT PAID · {money(balance)} balance remaining — you're all set to begin in the workroom.
        </div>
      ) : agreed ? (
        <div className="bg-primary-500/15 border-b border-primary-500/30 px-6 py-3 flex items-center justify-center gap-2 text-primary-300 font-bold text-sm" data-testid="proposal-agreed-banner">
          <CheckCircle2 size={17} /> AGREED &amp; SIGNED — this proposal is confirmed. Your workroom is ready.
        </div>
      ) : null}

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8 fade-in">
        {/* Hero image */}
        {p.hero_image && (
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl" data-testid="proposal-hero-image">
            <img src={api.proposalImageUrl(p.hero_image)} alt={p.title} className="w-full h-auto max-h-[360px] object-cover" />
          </div>
        )}

        {/* Header */}
        <div className="glass-panel rounded-2xl p-8">
          <div className="flex items-center gap-2 text-primary-400 text-xs font-bold uppercase tracking-wider mb-3"><FileSignature size={15} /> Proposal &amp; Agreement</div>
          <h1 className="text-3xl font-bold tracking-tight font-display" data-testid="proposal-view-title">{p.title}</h1>
          <div className="mt-4 text-sm text-zinc-400 space-y-1">
            {p.client_name && <div><span className="text-zinc-500">Prepared For:</span> {p.client_contact ? `${p.client_contact} · ` : ""}{p.client_name}</div>}
            <div><span className="text-zinc-500">Prepared By:</span> {p.prepared_by}</div>
          </div>
        </div>

        {p.summary && (
          <section className="glass-panel rounded-2xl p-8">
            <h2 className="text-lg font-bold text-white mb-3">Executive Summary</h2>
            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{p.summary}</p>
          </section>
        )}

        {(p.sections || []).map((s, i) => (
          <section key={s.id || i} className="glass-panel rounded-2xl p-8" data-testid={`proposal-section-${i}`}>
            <div className={`flex flex-col ${s.image ? "md:flex-row md:gap-6" : ""}`}>
              {s.image && (
                <div className="md:w-2/5 flex-shrink-0 mb-4 md:mb-0">
                  <img src={api.proposalImageUrl(s.image)} alt={s.title} className="w-full h-full max-h-64 md:max-h-none object-cover rounded-xl border border-white/10" data-testid={`proposal-section-image-${i}`} />
                </div>
              )}
              <div className={s.image ? "md:w-3/5" : "w-full"}>
                <h2 className="text-lg font-bold text-white">{s.title}</h2>
                {s.subtitle && <p className="text-sm font-bold text-primary-300 mt-1">{s.subtitle}</p>}
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-zinc-400">
                  {s.date && <span className="flex items-center gap-1.5"><Calendar size={13} /> {s.date}</span>}
                  {s.location && <span className="flex items-center gap-1.5"><MapPin size={13} /> {s.location}</span>}
                </div>
                {s.description && <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap mt-4">{s.description}</p>}
                {(s.responsibilities || []).filter(Boolean).length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Responsibilities</div>
                    <ul className="space-y-1.5">
                      {s.responsibilities.filter(Boolean).map((r, ri) => (
                        <li key={ri} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={15} className="text-green-400 mt-0.5 flex-shrink-0" /> {r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* Financial breakdown */}
        <section className="glass-panel rounded-2xl p-8">
          <h2 className="text-lg font-bold text-white mb-4">Investment &amp; Financial Breakdown</h2>
          <div className="space-y-2">
            {(p.line_items || []).map((li, i) => {
              const amt = Number(li.amount) || 0; const disc = Number(li.discount) || 0; const net = amt - disc;
              return (
                <div key={li.id || i} className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-sm text-zinc-200">{li.description}{disc > 0 && <span className="text-xs text-teal-400 ml-2">(−${disc.toLocaleString()} discount)</span>}</span>
                  <span className="text-sm font-bold text-white">${net.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary-500/30">
            <span className="font-bold text-white">Total Professional Fee</span>
            <span className="text-xl font-bold text-primary-300" data-testid="proposal-view-total">${total.toLocaleString()}</span>
          </div>
          {paid > 0 && (
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-green-400 font-bold">Paid to date</span>
              <span className="text-green-400 font-bold" data-testid="proposal-view-paid">−{money(paid)}</span>
            </div>
          )}
          {paid > 0 && !fullyPaid && (
            <div className="flex items-center justify-between text-sm font-bold text-white">
              <span>Remaining balance</span>
              <span data-testid="proposal-view-balance">{money(balance)}</span>
            </div>
          )}
          {(p.passthrough_items || []).filter((x) => x.description).length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Pass-Through Logistics (Direct Billed)</div>
              <ul className="space-y-1.5">
                {p.passthrough_items.filter((x) => x.description).map((x, i) => (
                  <li key={x.id || i} className="text-sm text-zinc-400 flex justify-between"><span>{x.description}</span>{x.estimate && <span className="text-zinc-500">{x.estimate}</span>}</li>
                ))}
              </ul>
            </div>
          )}
          {(p.retainer > 0 || p.payment_terms) && (
            <div className="mt-6 p-4 rounded-xl bg-black/30 border border-white/10">
              <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Payment Terms</div>
              {p.retainer > 0 && <div className="text-sm text-white font-bold">Retainer: ${Number(p.retainer).toLocaleString()}</div>}
              {p.payment_terms && <div className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{p.payment_terms}</div>}
            </div>
          )}
        </section>

        {/* Approve & Pay (no login required) */}
        <section className="glass-panel rounded-2xl p-8" data-testid="proposal-pay-section">
          {fullyPaid ? (
            <div className="text-center py-4">
              <CheckCircle2 size={40} className="text-green-400 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-white">Paid in Full — Thank You!</h2>
              <p className="text-sm text-zinc-400 mt-1">This proposal has been fully paid{p.signature?.name ? ` and approved by ${p.signature.name}` : ""}. We're all set to move forward.</p>
            </div>
          ) : (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">{agreed ? "Pay Your Balance" : "Approve This Proposal"}</h2>
              <p className="text-sm text-zinc-400 mb-5">{agreed ? "This proposal is confirmed — pay the balance below whenever you're ready. No account needed." : "Approve to lock in the deal and open your workroom. You can pay now or pay later."}</p>

              {p.signature_required && !p.signature && (
                <div className="mb-5 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20 space-y-3">
                  <div className="flex items-center gap-2 text-orange-400 text-xs font-bold uppercase tracking-wider"><ShieldCheck size={14} /> Signature Required (over $4,500)</div>
                  <input value={sig.name} onChange={(e) => setSig({ ...sig, name: e.target.value })} placeholder="Type your full name to approve" className="w-full px-4 py-3 rounded-xl dark-input text-sm" data-testid="proposal-signature-name" />
                  <input type="date" value={sig.date} onChange={(e) => setSig({ ...sig, date: e.target.value })} className="w-full px-4 py-3 rounded-xl dark-input text-sm" data-testid="proposal-signature-date" />
                </div>
              )}

              {!agreed && (
                <>
                  <button onClick={sign} disabled={signing} className="w-full py-4 mb-4 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition flex items-center justify-center disabled:opacity-60" data-testid="proposal-sign-btn">
                    {signing ? <><Loader2 size={18} className="mr-2 animate-spin" /> Approving…</> : <><Check size={18} className="mr-2" /> Approve &amp; Sign — Confirm the Deal</>}
                  </button>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">or pay now to get started</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                </>
              )}

              <div className="space-y-2.5 mb-4">
                {retainer > 0 && retainer < balance - 0.01 && paid <= 0 && (
                  <button onClick={() => setAmountMode("retainer")} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border text-left transition ${amountMode === "retainer" ? "border-primary-500 bg-primary-500/10" : "border-white/10 bg-black/20 hover:bg-white/5"}`} data-testid="pay-option-retainer">
                    <span className="text-sm font-bold text-white">Pay retainer to get started</span>
                    <span className="text-sm font-bold text-primary-300">{money(Math.min(retainer, balance))}</span>
                  </button>
                )}
                <button onClick={() => setAmountMode("full")} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border text-left transition ${amountMode === "full" ? "border-primary-500 bg-primary-500/10" : "border-white/10 bg-black/20 hover:bg-white/5"}`} data-testid="pay-option-full">
                  <span className="text-sm font-bold text-white">{paid > 0 ? "Pay remaining balance" : "Pay in full"}</span>
                  <span className="text-sm font-bold text-primary-300">{money(balance)}</span>
                </button>
                <button onClick={() => setAmountMode("custom")} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border text-left transition ${amountMode === "custom" ? "border-primary-500 bg-primary-500/10" : "border-white/10 bg-black/20 hover:bg-white/5"}`} data-testid="pay-option-custom">
                  <span className="text-sm font-bold text-white">Enter a custom amount</span>
                  {amountMode === "custom" ? (
                    <div className="flex items-center gap-1 text-primary-300 font-bold" onClick={(e) => e.stopPropagation()}>
                      <span>$</span>
                      <input type="number" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="0" max={balance} className="w-24 px-2 py-1 rounded-lg dark-input text-sm text-right" data-testid="pay-custom-input" />
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-500">up to {money(balance)}</span>
                  )}
                </button>
              </div>

              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note (optional)…" className="w-full px-4 py-3 rounded-xl dark-input text-sm mb-4" data-testid="proposal-pay-note" />

              <button onClick={pay} disabled={paying} className={`w-full py-4 rounded-xl font-bold transition flex items-center justify-center disabled:opacity-60 ${agreed ? "bg-primary-600 text-white hover:bg-primary-500" : "bg-white/5 border border-primary-500/40 text-primary-200 hover:bg-primary-500/10"}`} data-testid="proposal-pay-btn">
                {paying ? <><Loader2 size={18} className="mr-2 animate-spin" /> Redirecting to secure checkout…</> : <><CreditCard size={18} className="mr-2" /> Pay {money(chosenAmount())} Now</>}
              </button>
              <p className="text-center text-[11px] text-zinc-600 mt-3">Secured by Stripe · You'll be redirected to a secure checkout page.</p>
            </div>
          )}
        </section>

        {/* Discussion — refine the proposal before there's a deal; locks once paid */}
        <section className="glass-panel rounded-2xl p-8" data-testid="proposal-chat-section">
          <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><MessageSquare size={17} className="text-primary-400" /> Discussion</h2>
          {chatLocked ? (
            <div className="text-center py-6" data-testid="proposal-chat-locked">
              <Lock size={30} className="text-zinc-500 mx-auto mb-3" />
              <p className="text-sm text-zinc-300 font-bold">This proposal is confirmed — the discussion is locked.</p>
              <p className="text-sm text-zinc-500 mt-1">Now that we have a deal, everything continues in your workroom.</p>
              {workspaceHref && (
                <a href={workspaceHref} className="inline-flex items-center mt-4 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-500 transition" data-testid="proposal-chat-workspace-btn">Go to Workroom <ArrowRight size={15} className="ml-2" /></a>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-400 mb-4">Questions or changes? Message us here and we'll get this proposal exactly the way you need it — before you approve.</p>
              <div className="space-y-3 max-h-80 overflow-y-auto mb-4 pr-1 hide-scrollbar">
                {(p.chat || []).length === 0 && <p className="text-sm text-zinc-600 text-center py-4">No messages yet. Start the conversation below.</p>}
                {(p.chat || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`} data-testid="proposal-chat-msg">
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.sender === "client" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/10 text-zinc-100 rounded-bl-sm"}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 mb-0.5">{m.sender === "client" ? "You" : "DONDRÁE"}</div>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2">
                <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }} placeholder="Type your message…" className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" data-testid="proposal-chat-input" />
                <button onClick={sendChat} disabled={sendingChat} className="px-4 py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition disabled:opacity-60 flex items-center" data-testid="proposal-chat-send">{sendingChat ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
